import React from 'react';
import styled, { keyframes } from 'styled-components';

/**
 * Decorative wave that sits behind the page content. Traces itself in on load, then
 * scrolls sideways forever while its amplitude swells and subsides. Purely presentational
 * — hidden from AT.
 *
 * The path is a mirror-tiled wave: one half-period is cut from the original design curve
 * at two of its y-extrema, then reflected about a vertical axis through the cut. Because
 * the tangent at an extremum is horizontal, the mirror joins with no kink, and
 * mirror∘mirror is a pure translation — so shifting by exactly one period (2229.38 user
 * units) loops invisibly.
 *
 * The gradient paints a static `<rect>` masked by the moving wave rather than stroking
 * the path directly: a `userSpaceOnUse` gradient inherits ancestor transforms, so painted
 * on the path the red accent would ride along with it and pop back at every loop restart.
 *
 * `pathLength="1"` normalises the path so the draw-on reveal is pure CSS
 * (`stroke-dasharray: 1`) with no `getTotalLength()` measurement.
 */

/**
 * One mirrored tile-pair, in viewBox user units. Inside an SVG a CSS `px` in a
 * `transform` is one user unit, so translating by exactly this makes the loop invisible.
 */
const WAVE_PERIOD = '2229.38px';

const waveDraw = keyframes`
  to {
    stroke-dashoffset: 0;
  }
`;

const waveScroll = keyframes`
  from {
    transform: translate3d(0, 0, 0);
  }
  to {
    transform: translate3d(calc(-1 * ${WAVE_PERIOD}), 0, 0);
  }
`;

const waveSwell = keyframes`
  from {
    /* Amplitude swell, measured from the off-screen deep-trough baseline. */
    transform: scaleY(0.9);
  }
  to {
    transform: scaleY(1.12);
  }
`;

const WaveHost = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;

  .wave__svg {
    position: absolute;
    left: -6%;
    bottom: -6%;
    width: 118%;
    height: auto;
    /**
     * The wave runs to x ≈ 4459 and dips to y ≈ 1061 — both past the viewBox. Let it bleed;
     * the host does the clipping.
     */
    overflow: visible;
  }

  /** Scroll and swell live on separate groups so neither overwrites the other's transform. */
  .wave__scroll {
    will-change: transform;
    animation: ${waveScroll} 34s linear infinite;
  }

  .wave__breathe {
    transform-box: view-box;
    /**
     * Anchored on the deep trough (which sits below the fold), so the whole visible wave
     * rises and falls from a fixed waterline.
     */
    transform-origin: 0 1061.48px;
    /**
     * 11s alternating is a 22s cycle against a 34s scroll — deliberately non-harmonic, so
     * the two never re-align and the drift never reads as mechanical.
     */
    animation: ${waveSwell} 11s ease-in-out infinite alternate;
  }

  .wave__path {
    stroke-dasharray: 1;
    stroke-dashoffset: 1;
    /**
     * Only the first ~36% of the path (x ∈ [0, 1629]) is ever on screen, so the duration is
     * scaled up to keep the visible sweep at the original ~1.9s. Linear, because an eased
     * curve would finish that visible part in a third of the time.
     */
    animation: ${waveDraw} 5200ms 250ms linear forwards;
  }

  @media (prefers-reduced-motion: reduce) {
    .wave__scroll,
    .wave__breathe {
      animation: none;
    }

    .wave__path {
      animation: none;
      stroke-dashoffset: 0;
    }
  }
`;

export const BackgroundWave: React.FC = () => (
  <WaveHost aria-hidden="true">
    <svg
      className="wave__svg"
      viewBox="0 0 1629 953"
      fill="none"
      preserveAspectRatio="xMinYMax meet"
      focusable="false"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient
          id="background-wave-gradient"
          x1="2046.1"
          y1="565.505"
          x2="1525.35"
          y2="1622.93"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FAF3E8" />
          <stop offset="0.669924" stopColor="#DC1E29" />
          <stop offset="0.733378" stopColor="#A5161F" />
          <stop offset="1" stopColor="#FAF3E8" />
        </linearGradient>

        {/* Nested groups so stroke-dashoffset, translateX and scaleY never fight over
            a single transform. */}
        <mask
          id="background-wave-mask"
          maskUnits="userSpaceOnUse"
          x="-200"
          y="380"
          width="2100"
          height="1060"
        >
          <g className="wave__breathe">
            <g className="wave__scroll">
              <path
                className="wave__path"
                pathLength="1"
                vectorEffect="non-scaling-stroke"
                stroke="#fff"
                strokeWidth="2"
                fill="none"
                d="M0 888.61C294.72 888.61 282.3 721.95 459.69 683.14C669.85 637.15 890.75 1061.48 1114.69 1061.48C1338.63 1061.48 1559.53 637.15 1769.69 683.14C1947.08 721.95 1934.66 888.61 2229.38 888.61C2524.1 888.61 2511.68 721.95 2689.07 683.14C2899.23 637.15 3120.13 1061.48 3344.07 1061.48C3568.01 1061.48 3788.91 637.15 3999.07 683.14C4176.46 721.95 4164.04 888.61 4458.76 888.61"
              />
            </g>
          </g>
        </mask>
      </defs>

      <rect
        className="wave__ink"
        x="-20"
        y="560"
        width="1680"
        height="700"
        fill="url(#background-wave-gradient)"
        mask="url(#background-wave-mask)"
      />
    </svg>
  </WaveHost>
);
